import 'simplebar-react/dist/simplebar.min.css';

import { NewReleases as NewReleasesIcon } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import NaturePeopleIcon from '@mui/icons-material/NaturePeople';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as React from 'react';
import { Link, useMatch, useNavigate } from 'react-router-dom';
import SimpleBar from 'simplebar-react';

import { getChipStatusProps } from '@/components/StatusIcon/statusMapping';
import { getWorstDevice } from '@/components/StatusIcon/statusMapping';
import useMixPanel from '@/hooks/useMixPanel';

import { createHomeRoute, createStructureRoute, createUnitRoute, routes } from '../../app-routes';
import { getUserId } from '../../auth';
import StructureForm from '../../Structures/Create';
// Types
import type { Structure } from '../../Structures/types';
import { useStructure, useStructures } from '../../Structures/useStructures';
import { getUnitStatus } from '../../Structures/util';
import { batchCreate as batchCreateUnits, create as createUnit } from '../../Units/api';
import { FormDialog as AddUnitDialog } from '../../Units/FormDialog';
import { FormBulkUnit } from '../../Units/FormDialog/bulk';
import type { AddUnitFormValues, Unit } from '../../Units/types';
import { ErrorBoundary } from '../ErrorBoundary';
import { StatusIcon } from '../StatusIcon';
import { DrawerHeader, MiniDrawer } from './components';
import { DrawerSearch, searchRowHeight } from './DrawerSearch';
import type { MatchField } from './search';
import { filterStructures, firstResult, normalizeQuery } from './search';
import { useIsTruncated } from './useIsTruncated';
import { drawerWidth } from './util';

/** Selector for the single-line label inside a ListItemText, used to detect ellipsis. */
const PRIMARY_LABEL_SELECTOR = '.MuiListItemText-primary';

// Constants //////////////////////////////////////////////////////////////////

const listItemStyles = makeStyles((theme) => ({
  link: {
    textDecoration: 'none',
  },
  listItemText: {
    color: 'white',
  },
  listItemTextPrimary: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  // The secondary line can be a full street address or a meter serial while searching,
  // which is the reason the row matched, so it wraps instead of running off the drawer.
  // Wrapping is safe in the collapsed rail only because the list is laid out at the
  // full drawer width in both states (see the width on the List below).
  listItemTextSecondary: {
    color: 'white',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
  },
  nestedListItem: {
    paddingLeft: theme.spacing(4),
  },
}));

// Structure List Item ////////////////////////////////////////////////////////

type StructureListItemProps = {
  structure: Structure;
  isSelected: boolean;
  /** Line under the property name: the unit count, or the matching address while searching. */
  secondaryText: string;
  onClick: () => void;
  isExpanded: boolean;
  sidebarOpen: boolean;
};

const StructureListItem = ({
  structure,
  isSelected,
  secondaryText,
  onClick,
  isExpanded,
  sidebarOpen,
}: StructureListItemProps) => {
  const classes = listItemStyles();
  // The name is a single ellipsis line. When it is cut off, the full name is offered on
  // hover, focus, and long-press; rows that fit get no tooltip so the list stays quiet.
  const { ref, truncated } = useIsTruncated<HTMLDivElement>(structure.name, PRIMARY_LABEL_SELECTOR);
  // In the collapsed rail the label is clipped rather than ellipsised, so always offer it there.
  const showTooltip = truncated || !sidebarOpen;

  return (
    <ErrorBoundary>
      <Link to={createStructureRoute(structure.id)} className={classes.link} onClick={onClick}>
        <Tooltip
          title={structure.name}
          placement="right"
          disableHoverListener={!showTooltip}
          disableFocusListener={!showTooltip}
          disableTouchListener={!showTooltip}
        >
          <ListItem button key={structure.name} selected={isSelected} ref={ref}>
            <ListItemIcon>
              <BusinessIcon htmlColor="white" />
            </ListItemIcon>
            <ListItemText
              primary={structure.name}
              secondary={secondaryText}
              classes={{
                root: classes.listItemText,
                primary: classes.listItemTextPrimary,
                secondary: classes.listItemTextSecondary,
              }}
            />
            {isExpanded ? <ExpandLess htmlColor="white" /> : <ExpandMore htmlColor="white" />}
          </ListItem>
        </Tooltip>
      </Link>
    </ErrorBoundary>
  );
};

// Unit List Item /////////////////////////////////////////////////////////////

type UnitListItemProps = {
  structureId: string;
  structureName: string;
  unit: Unit;
  isSelected: boolean;
  sidebarOpen: boolean;
  /** Shown under the unit name when a search matched something other than the name, e.g. a meter serial. */
  secondaryText: string | null;
  onClick: () => void;
};

interface IChipStatus {
  bgColor: string;
  text: string;
  color: string;
  type: 'default' | 'leak' | 'sensor' | 'connection';
}

export const ChipStatus = ({ bgColor, text, color, type }: IChipStatus) => {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: bgColor,
        color: color,
        borderRadius: '8px',
        fontWeight: 'bold',
        padding: '4px 8px',
        fontSize: '0.875rem',
        textTransform: 'uppercase',
        boxShadow: 1,
        whiteSpace: 'nowrap',
      }}
    >
      <Stack direction={'row'} gap={1}>
        <StatusIcon color={color} type={type} />
        {text !== '' && <Typography fontSize={12}>{text}</Typography>}
      </Stack>
    </Box>
  );
};

const UnitListItem = ({
  structureId,
  structureName: _structureName, // destructuring alias to avoid unused variable warning
  unit,
  isSelected,
  sidebarOpen,
  secondaryText,
  onClick,
}: UnitListItemProps) => {
  const classes = listItemStyles();
  const unitStatus = getUnitStatus(unit);
  // Same overflow-only tooltip as the property row; see StructureListItem.
  const { ref, truncated } = useIsTruncated<HTMLDivElement>(unit.name, PRIMARY_LABEL_SELECTOR);
  const showTooltip = truncated || !sidebarOpen;

  const RenderDeviceStatus = (device, onlyIcon = false) => {
    const props = getChipStatusProps(device.device_status, device.device_status_strings?.[0]);
    if (props) {
      return <ChipStatus {...props} text={onlyIcon ? '' : device.name} />;
    }
    return null;
  };
  return (
    <ErrorBoundary>
      <Link to={createUnitRoute(structureId, unit.id)} className={classes.link} onClick={onClick}>
        <Tooltip
          title={unit.name}
          placement="right"
          disableHoverListener={!showTooltip}
          disableFocusListener={!showTooltip}
          disableTouchListener={!showTooltip}
        >
          <ListItem
            button
            dense
            key={unit.name}
            className={sidebarOpen ? classes.nestedListItem : ''}
            selected={isSelected}
            ref={ref}
          >
            <ListItemIcon>
              {unit.unit_flags.includes('COMMON_AREA') ? (
                <NaturePeopleIcon htmlColor="white" />
              ) : (
                <MeetingRoomIcon htmlColor="white" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={unit.name}
              secondary={secondaryText}
              classes={{
                root: classes.listItemText,
                primary: classes.listItemTextPrimary,
                secondary: classes.listItemTextSecondary,
              }}
            />
            {unitStatus !== 'NO_DEVICES' && unit.devices.length > 1 ? (
              <Stack direction={'row'} gap={0.5} alignItems="center">
                {(() => {
                  const worstDevice = getWorstDevice(unit.devices);
                  return worstDevice ? RenderDeviceStatus(worstDevice, true) : null;
                })()}
                <Box
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '4px',
                    padding: '0px 4px',
                    minWidth: '24px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography color="white" fontSize="12px" fontWeight="500">
                    +{unit.devices.length - 1}
                  </Typography>
                </Box>
              </Stack>
            ) : (
              unit.devices && unit.devices.map((i) => RenderDeviceStatus(i, true))
            )}
          </ListItem>
        </Tooltip>
      </Link>
    </ErrorBoundary>
  );
};

// Component //////////////////////////////////////////////////////////////////

const useStyles: () => {
  bottomToolbarButtonContainer: string;
  bottomToolbarGradient: string;
  bottomToolbarRoot: string;
  drawer: string;
  homeListItem: string;
  link: string;
  list: string;
  listItemText: string;
  nestedListItem: string;
} = makeStyles((theme) => ({
  bottomToolbarRoot: {
    position: 'absolute',
    bottom: 0,
    width: '100%',
    backgroundColor: 'transparent',
    justifyContent: 'center',
    flexDirection: 'column',
    paddingLeft: 0,
    paddingRight: 0,
  },
  bottomToolbarGradient: {
    backgroundImage: 'linear-gradient(to bottom, rgba(86, 86, 86, 0), rgba(86, 86, 86, 1))',
    height: theme.spacing(2),
    width: '100%',
  },
  bottomToolbarButtonContainer: {
    backgroundColor: 'rgba(86, 86, 86, 1)',
    width: '100%',
    height: theme.spacing(6),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  drawer: {
    maxWidth: drawerWidth,
    minWidth: drawerWidth,
    backgroundColor: theme.palette.grey[800], //'rgb(86, 86, 86)'
  },
  homeListItem: {
    height: theme.spacing(8),
  },
  link: {
    textDecoration: 'none',
  },
  list: {
    paddingTop: 0,
  },
  listItemText: {
    color: 'white',
  },
  nestedListItem: {
    paddingLeft: theme.spacing(4),
  },
}));

interface DrawerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const Drawer = ({ open, setOpen }: DrawerProps) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const hMatch = useMatch(routes.home);
  const sMatch = useMatch(routes.property);
  const uMatch = useMatch(routes.unit);
  // Guard against duplicate /release-notes history entries when the version button
  // is spam-clicked. React Router v6's `navigate(path)` always pushes a new entry
  // even if it equals the current location, so back-button presses then have to
  // chew through every duplicate before reaching the prior page.
  const isOnReleaseNotes = useMatch('/release-notes');
  const structureId = sMatch?.params?.structureId || uMatch?.params?.structureId || '';
  const unitId = uMatch?.params?.unitId;
  const { structures } = useStructures();
  const { structure, mutate: updateStructure } = useStructure(structureId);

  const [expandedProperties, setExpandedProperties] = React.useState<Record<string, boolean>>({});
  const [isAddStructureFormOpen, setIsAddStructureFormOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const isFiltering = normalizeQuery(query) !== '';
  const matches = React.useMemo(() => filterStructures(structures, query), [structures, query]);

  // Clear expanded properties when navigating away from all properties (e.g., to home)
  React.useEffect(() => {
    if (!structureId) {
      setExpandedProperties({});
    }
  }, [structureId]);
  const [type, setType] = React.useState<'UNIT' | 'COMMON_AREA'>('UNIT');
  const [isAddUnitOpen, setIsAddUnitOpen] = React.useState(false);
  const [isBulkUnitOpen, setIsBulkUnitOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const { trackEvent } = useMixPanel();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openMenuUnit = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  /**
   * Record a click on a search result. The query text itself is customer data
   * (property and unit names), so only its length and the match shape are sent.
   */
  const trackSearchResult = (
    resultType: 'Property' | 'Unit',
    matchedBy: MatchField | null,
    via: 'Click' | 'Enter',
  ) => {
    if (!isFiltering) return;
    trackEvent('Sidebar Search Result Selected', {
      ResultType: resultType,
      MatchedBy: matchedBy ?? 'Parent',
      SelectedVia: via,
      QueryLength: normalizeQuery(query).length,
      PropertyResultCount: matches.length,
      UnitResultCount: matches.reduce((n, m) => n + m.units.length, 0),
    });
  };

  /** Enter in the search box goes to the first matching unit, else the first matching property. */
  const handleSearchSubmit = () => {
    if (!isFiltering) return;
    const target = firstResult(matches);
    if (!target) return;
    const first = matches[0];
    // Keep the destination's property open so the row is still there when the search is cleared.
    setExpandedProperties({ [target.structureId]: true });
    if (target.unitId) {
      trackSearchResult('Unit', first.units[0].matchedBy, 'Enter');
      navigate(createUnitRoute(target.structureId, target.unitId));
    } else {
      trackSearchResult('Property', first.matchedBy, 'Enter');
      navigate(createStructureRoute(target.structureId));
    }
  };

  const handleSubmitAddUnit = async ({
    name,
    pipeDiameter,
    pipeMaterial,
    unitFlag,
  }: AddUnitFormValues) => {
    if (!structure || !structureId) return;
    // eslint-disable-next-line no-console
    console.log('Add Unit', name, pipeDiameter, pipeMaterial, unitFlag);
    setLoading(true);
    try {
      const newUnit = await createUnit(
        await getUserId(),
        structureId,
        structure.address,
        name,
        structure.units.length,
        unitFlag,
      );
      await updateStructure({
        ...structure,
        units: [...structure.units, newUnit],
      });
      trackEvent('Unit Created', {
        PropertyName: structure.name,
        PropertyID: structure.id,
        UnitName: newUnit.name,
        UnitID: newUnit.id,
      });
      setIsAddUnitOpen(false);
    } catch (err) {
      // Error already shown by API layer, keep dialog open
      console.error('Failed to create unit:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAddUnits = async (
    body: { structure_id: string; units: Array<{ name: string; unit_flags: string[] }> },
    fileType: string,
  ) => {
    const result = await batchCreateUnits(body);
    if (structure) {
      await updateStructure({
        ...structure,
        units: [...structure.units, ...result.units],
      });
    }
    trackEvent('Units Imported', {
      PropertyName: structure?.name,
      PropertyID: structure?.id,
      Count: body.units.length,
      FileType: fileType,
    });
  };

  return (
    <ErrorBoundary>
      <MiniDrawer anchor={'left'} variant="permanent" open={open}>
        <DrawerHeader>
          <IconButton onClick={() => setOpen(false)}>
            <ChevronLeftIcon htmlColor="#fff" />
          </IconButton>
        </DrawerHeader>
        <Divider />
        <DrawerSearch
          open={open}
          onOpen={() => setOpen(true)}
          query={query}
          onQueryChange={setQuery}
          onSubmit={handleSearchSubmit}
        />
        {/*
          Drawer paper already clips horizontal overflow (closedMixin in components.tsx),
          but SimpleBar's inner .simplebar-content-wrapper has its own runtime-applied
          inline overflow styles that re-introduce a horizontal scrollbar when ListItem
          labels measure wider than the ~65px mini-drawer paper. Forwarding overflowX:
          hidden via scrollableNodeProps races with SimpleBar's own inline style writes,
          so pin the override here via CSS class selectors with !important so it wins
          regardless of SimpleBar's runtime mutations. We also hide the horizontal
          track itself so SimpleBar doesn't even render it.
        */}
        <Box
          sx={{
            '& .simplebar-content-wrapper': { overflowX: 'hidden !important' },
            '& .simplebar-track.simplebar-horizontal': { display: 'none !important' },
          }}
        >
          <SimpleBar style={{ maxHeight: `calc(100vh - 56px - 70px - ${searchRowHeight}px)` }}>
            {/*
              The list keeps the full drawer width even while collapsed, so rows are laid
              out once and merely clipped by the rail. Letting the list follow the paper's
              animated width made wrapped lines reflow mid-transition and the rows below
              them jump down and back up on every open.
            */}
            <List className={classes.list} sx={{ width: drawerWidth }}>
              <Link to={createHomeRoute()} className={classes.link}>
                <ListItem button selected={Boolean(hMatch)} className={classes.homeListItem}>
                  <ListItemIcon>
                    <HomeWorkIcon htmlColor="white" />
                  </ListItemIcon>
                  <ListItemText primary={'Home'} className={`${classes.listItemText} mp-unmask`} />
                </ListItem>
              </Link>
              {isFiltering && matches.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary="No matches"
                    secondary="Try a property name, unit name, address, or meter serial"
                    className="mp-unmask"
                    // The drawer is nowrap so labels truncate; this hint has to wrap to stay readable.
                    sx={{ whiteSpace: 'normal' }}
                    classes={{
                      root: classes.listItemText,
                      secondary: classes.listItemText,
                    }}
                  />
                </ListItem>
              )}
              {structures
                ? matches.map(({ structure: s, matchedBy, matchText, units, otherUnits }) => {
                    // While searching, a property with matching units is always open and
                    // lists just those units. A property that matched on its own starts
                    // closed and its chevron discloses every unit, so a name hit is still a
                    // way into the property without clearing the search.
                    const hasUnitMatches = isFiltering && units.length > 0;
                    const isExpanded = hasUnitMatches
                      ? true
                      : isFiltering
                      ? Boolean(expandedProperties[s.id])
                      : structureId === s.id && Boolean(expandedProperties[s.id]);
                    const visibleUnits = isFiltering && !hasUnitMatches ? otherUnits : units;
                    return (
                      <React.Fragment key={s.id}>
                        <StructureListItem
                          structure={s}
                          secondaryText={
                            matchedBy === 'address' && matchText
                              ? matchText
                              : `${s.units.length} Units`
                          }
                          onClick={() => {
                            trackSearchResult('Property', matchedBy, 'Click');
                            setExpandedProperties((prev) => {
                              const isCurrentlyExpanded = prev[s.id];
                              if (isCurrentlyExpanded) {
                                // If clicking on an already expanded property, close it
                                return { [s.id]: false };
                              } else {
                                // If clicking on a collapsed property, close all others and open this one
                                return { [s.id]: true };
                              }
                            });
                          }}
                          isSelected={s.id === structureId}
                          isExpanded={isExpanded}
                          sidebarOpen={open}
                        />
                        <Collapse in={isExpanded}>
                          <List>
                            {visibleUnits.map(
                              ({ unit: u, matchedBy: unitMatchedBy, matchText: unitMatchText }) => {
                                return (
                                  <UnitListItem
                                    key={u.id}
                                    structureId={s.id}
                                    structureName={s.name}
                                    unit={u}
                                    isSelected={u.id === unitId}
                                    sidebarOpen={open}
                                    secondaryText={unitMatchText}
                                    onClick={() => {
                                      trackSearchResult('Unit', unitMatchedBy, 'Click');
                                      // Keep this property open so the unit's row survives clearing the search.
                                      if (isFiltering) setExpandedProperties({ [s.id]: true });
                                    }}
                                  />
                                );
                              },
                            )}
                            {!isFiltering && (
                              <Button
                                sx={{
                                  width: '100%',
                                  margin: 0,
                                  padding: 0,
                                  textTransform: 'capitalize',
                                }}
                                onClick={handleClick}
                              >
                                <ListItem
                                  // button
                                  // onClick={() => setIsAddUnitOpen(true)}
                                  className={open ? classes.nestedListItem : ''}
                                >
                                  <ListItemIcon>
                                    <AddIcon htmlColor="white" />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary="Add"
                                    classes={{
                                      root: classes.listItemText,
                                    }}
                                  />
                                </ListItem>
                              </Button>
                            )}
                          </List>
                        </Collapse>
                        <Menu
                          id="basic-menu"
                          anchorEl={anchorEl}
                          open={openMenuUnit}
                          onClose={handleClose}
                          MenuListProps={{
                            'aria-labelledby': 'basic-button',
                          }}
                        >
                          <MenuItem
                            onClick={() => {
                              handleClose();
                              setIsAddUnitOpen(true);
                              setType('UNIT');
                            }}
                          >
                            <MeetingRoomIcon htmlColor="black" sx={{ marginRight: 2 }} />
                            Add Unit
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              handleClose();
                              setIsAddUnitOpen(true);
                              setType('COMMON_AREA');
                            }}
                          >
                            <NaturePeopleIcon htmlColor="black" sx={{ marginRight: 2 }} />
                            Add Other
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              handleClose();
                              setIsBulkUnitOpen(true);
                            }}
                          >
                            <UploadFileIcon htmlColor="black" sx={{ marginRight: 2 }} />
                            Import Units
                          </MenuItem>
                        </Menu>
                      </React.Fragment>
                    );
                  })
                : null}
              {!isFiltering && (
                <ListItem button onClick={() => setIsAddStructureFormOpen(true)}>
                  <ListItemIcon>
                    <AddIcon htmlColor="white" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Add Property"
                    className={`${classes.listItemText} mp-unmask`}
                  />
                </ListItem>
              )}
            </List>
          </SimpleBar>
        </Box>
        <Box sx={{ position: 'absolute', bottom: 16, left: 16 }}>
          <Tooltip title={`${process.env.REACT_APP_VERSION ?? 'unknown'}`}>
            <IconButton
              onClick={() => {
                if (!isOnReleaseNotes) navigate('/release-notes');
              }}
              sx={{
                padding: 1,
                color: 'white',
              }}
            >
              <NewReleasesIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <StructureForm
          open={isAddStructureFormOpen}
          onClose={() => setIsAddStructureFormOpen(false)}
        />
        <AddUnitDialog
          addOrEdit={'ADD'}
          type={type}
          name={`${(structure?.units.length || 0) + 1}`}
          pipeDiameter={1}
          pipeMaterial={'COPPER-M'}
          open={isAddUnitOpen}
          onClose={() => setIsAddUnitOpen(false)}
          onSubmit={handleSubmitAddUnit}
          loading={loading}
        />
        <FormBulkUnit
          open={isBulkUnitOpen}
          onClose={() => setIsBulkUnitOpen(false)}
          existingUnits={structure?.units || []}
          structureId={structureId || ''}
          onSubmit={handleBulkAddUnits}
        />
      </MiniDrawer>
    </ErrorBoundary>
  );
};
